import "@/App.css";
import { lazy, Suspense } from "react";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Toaster } from "sonner";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import Layout from "@/components/Layout";
import Login from "@/pages/Login";

const ResetPassword = lazy(() => import("@/pages/ResetPassword"));
const Dashboard = lazy(() => import("@/pages/Dashboard"));
const Banco = lazy(() => import("@/pages/Banco"));
const Magazzino = lazy(() => import("@/pages/Magazzino"));
const Riparazioni = lazy(() => import("@/pages/Riparazioni"));
const Preventivi = lazy(() => import("@/pages/Preventivi"));
const Resi = lazy(() => import("@/pages/Resi"));
const Spese = lazy(() => import("@/pages/Spese"));
const Sospesi = lazy(() => import("@/pages/Sospesi"));
const StoricoChiusure = lazy(() => import("@/pages/StoricoChiusure"));
const Utenti = lazy(() => import("@/pages/Utenti"));
const Notifiche = lazy(() => import("@/pages/Notifiche"));
const MovimentiMagazzino = lazy(() => import("@/pages/MovimentiMagazzino"));
const Home = lazy(() => import("@/pages/Home"));
const Archivio = lazy(() => import("@/pages/Archivio"));
const AnagraficheClienti = lazy(() => import("@/pages/AnagraficheClienti"));
const Contabilita = lazy(() => import("@/pages/Contabilita"));
const SospesiPersonali = lazy(() => import("@/pages/SospesiPersonali"));

function Protected({ children, adminOnly, perm }) {
  const { user, ready, isAdmin, can } = useAuth();
  if (!ready) return <div className="p-10 text-muted-foreground">Caricamento…</div>;
  if (!user) return <Navigate to="/login" replace />;
  if (adminOnly && !isAdmin) return <Navigate to="/" replace />;
  if (perm && !can(perm)) return <Navigate to="/" replace />;
  return children;
}

function PageLoader() {
  return <div className="p-10 text-muted-foreground">Caricamento…</div>;
}

function AppRoutes() {
  const { user, ready } = useAuth();
  return (
    <Suspense fallback={<PageLoader />}>
      <Routes>
        <Route path="/login" element={ready && user ? <Navigate to="/" replace /> : <Login />} />
        <Route path="/reset-password" element={<ResetPassword />} />
        <Route
          path="/"
          element={
            <Protected>
              <Layout />
            </Protected>
          }
        >
          <Route index element={<Home />} />
          <Route path="dashboard" element={<Protected perm="dashboard"><Dashboard /></Protected>} />
          <Route path="banco" element={<Protected perm="banco"><Banco /></Protected>} />
          <Route path="magazzino" element={<Protected perm="magazzino"><Magazzino /></Protected>} />
          <Route path="riparazioni" element={<Protected perm="riparazioni"><Riparazioni /></Protected>} />
          <Route path="preventivi" element={<Protected perm="preventivi"><Preventivi /></Protected>} />
          <Route path="resi" element={<Protected perm="resi"><Resi /></Protected>} />
          <Route path="sospesi" element={<Protected perm="sospesi"><Sospesi /></Protected>} />
          <Route path="chiusure" element={<Protected perm="chiusure"><StoricoChiusure /></Protected>} />
          <Route path="archivio" element={<Protected perm="archivio"><Archivio /></Protected>} />
          <Route path="spese" element={<Protected perm="spese"><Spese /></Protected>} />
          <Route path="utenti" element={<Protected adminOnly><Utenti /></Protected>} />
          <Route path="clienti" element={<Protected perm="clienti"><AnagraficheClienti /></Protected>} />
          <Route path="contabilita" element={<Protected perm="contabilita"><Contabilita /></Protected>} />
          <Route path="sospesi-personali" element={<Protected perm="sospesi_personali"><SospesiPersonali /></Protected>} />
          <Route path="notifiche" element={<Protected perm="notifiche"><Notifiche /></Protected>} />
          <Route path="movimenti-magazzino" element={<Protected adminOnly><MovimentiMagazzino /></Protected>} />
        </Route>
      </Routes>
    </Suspense>
  );
}


export default function App() {
  return (
    <div className="App">
      <AuthProvider>
        <BrowserRouter>
          <AppRoutes />
          <Toaster position="top-right" richColors />
        </BrowserRouter>
      </AuthProvider>
    </div>
  );
}
